import { createLoggers } from '@highlight-cp/logger';

const { debug, warn } = createLoggers({ tag: 'hubspot' });

interface HubSpotVisitorTokenResponse {
  hubspotPortalId: string;
  token: string;
  expiresAt: number;
}

interface HubspotExtendedWindow extends Window {
  hsConversationsSettings?: {
    identificationEmail?: string;
    identificationToken?: string;
  };
}

/**
 *  Identify the current visitor in Hubspot chat widget.
 *
 *  @see {@link https://developers.hubspot.com/docs/api-reference/conversations-visitor-identification-v3/guide}
 */
export async function maybeIdentifyHubspotVisitor(params: { email: string; userId: string }) {
  const { email, userId } = params;
  if (!email || !userId) {
    debug('Skipping identification of empty session', { email, userId });
    return;
  }

  const win: HubspotExtendedWindow = window;
  if (win.hsConversationsSettings) {
    // Bail out if someone/something is already attempting identification. We
    // don't currently have a mechanism for "re-identifying" if a session
    // changes (e.g. via impersonation); if/when we need it, we'll need a
    // more clever latch.
    return;
  }

  const identify = (payload: HubSpotVisitorTokenResponse) => {
    const { hubspotPortalId, token } = payload;

    win.hsConversationsSettings = {
      identificationEmail: email,
      identificationToken: token,
    };

    const hsSnippetSrc = `//js.hs-scripts.com/${hubspotPortalId}.js`;
    const s = document.createElement('script');
    s.src = hsSnippetSrc;
    s.id = 'hs-script-loader';
    s.async = true;
    document.body.appendChild(s);
  };

  try {
    debug('Identifying visitor in HubSpot', { userId });

    const response = await fetch('/api/hubspot/visitor-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, userId }),
    });

    if (!response.ok) {
      warn('Failed to identify visitor in HubSpot', {
        status: response.status,
        userId,
      });
      return;
    }

    const payload: HubSpotVisitorTokenResponse = await response.json();
    identify(payload);
  } catch (err) {
    const error = err as Error;
    warn('Error identifying visitor in HubSpot', {
      error: error.message,
      userId,
    });
  }
}
